/**
 * The sketch policy (PRP-SKETCH-FIELD-1 work item 1), typed.
 *
 * Every threshold, list and default the core uses comes from
 * `sketch-policy.json`. No core file writes one of these values as a literal;
 * `__tests__/policy-literals.test.ts` enforces that.
 */
import raw from './sketch-policy.json';
import type { BifoldSide, CeilingType, DoorPanels, MeasurementSource, MeasurementV2, MissingKind, RoomGeometryV2 } from '../../../storage/schemas';

export interface ShapeSize { width_ft: number; depth_ft: number; notch_width_ft?: number; notch_depth_ft?: number }

/** FIELD-4 K9: the kinds of wall-drag snap target, named in `snap_priority`. */
export type SnapKind = 'one_wall' | 'corner' | 'colinear' | 'equal_size'; // policy-literal-ok: the snap kind vocabulary (data contract)

export interface SketchPolicy {
  interior_wall_in: number;
  exterior_wall_in: number;
  door_open_angle_deg: number;
  door_height_in: number;
  window_sill_in: number;
  snap_flush_ft: number;
  snap_corner_px: number;
  long_press_ms: number;
  drag_slop_px: number;
  undo_limit: number;
  level_default: string;
  ceiling_default_ft: number;
  min_touch_pt: number;
  shapes: { rectangle: ShapeSize; L: ShapeSize };
  room_names: string[];
  numbered_room_names: string[];
  new_room_name: string;
  door_types: string[];
  door_default_interior: { type: string; width_ft: number };
  door_default_exterior: { type: string; width_ft: number };
  window_styles: string[];
  window_materials: string[];
  window_options: { grid: string[]; glazing: string[]; impact: string[]; grade: string[]; trim: string[] };
  window_default: {
    width_ft: number; height_ft: number; style: string; material: string; grid: string;
    glazing: string; impact: string; grade: string; trim: string;
  };
  missing_wall_trim: string[];
  missing_wall_default: { width_ft: number; trim: string };
  esx_door_type: Record<string, number>;
  esx_double_door: string[];
  esx_opening_type: Record<string, number>;
  snap_unit_in: number;
  same_reading_tol_in: number;
  shared_wall_tol_in: number;
  shared_min_overlap_ft: number;
  facing_dot_max: number;
  parallel_dot_min: number;
  mismatch_min_overlap_ft: number;
  mismatch_gap_min_ft: number;
  mismatch_gap_max_ft: number;
  mismatch_tol_in: number;
  snap_min_ft: number;
  snap_min_overlap_ft: number;
  snap_corner_reach_ft: number;
  snap_guide_overhang_ft: number;
  break_clearance_ft: number;
  bump_min_ft: number;
  split_clearance_ft: number;
  opening_clearance_ft: number;
  min_segment_ft: number;
  opposite_min_ft: number;
  duplicate_offset_ft: number;
  ceiling_min_ft: number;
  across_min_ft: number;
  length_min_ft: number;
  opening_min_width_ft: number;
  opening_min_height_ft: number;
  label_hide_margin_ft: number;
  // PRP-SKETCH-FIELD-2 item 1.
  exterior_wall_default_in: number;
  wall_thickness_choices_in: number[];
  /** The Custom thickness wheels' step (inches); every other wheel steps by `snap_unit_in`. */
  wall_thickness_snap_in: number;
  legacy_interior_wall_in: number;
  opening_default: { kind: MissingKind; width_ft: number };
  missing_kinds: Record<MissingKind, MissingKindPolicy>;
  open_to_kind: MissingKind;
  island_kinds: { side: MissingKind; over: MissingKind };
  /** A FIELD-1 missing wall (no kind): the whole wall reads as this kind ... */
  legacy_missing_kind: MissingKind;
  /** ... and part of a wall as this one (FIELD-1 D4: exported floor to ceiling). */
  legacy_missing_kind_partial: MissingKind;
  island_height_in: number;
  beyond_depth_ft: number;
  beyond_match_max_ft: number;
  long_press_room_clear_ft: number;
  ceiling_types: Record<CeilingType, { label: string; fields: string[]; orientation: boolean }>;
  first_room_exterior_door: boolean;
  // PRP-SKETCH-FIELD-3 item 1: the one typed declaration of the FIELD-3 keys (doors.ts,
  // closet.ts, wallRemove.ts and labels.ts read them from here; B-7355 item 3).
  /** J7: a dragged segment within this of a neighbour's line snaps to it; Remove break's reach. */
  break_snap_in: number;
  /** H8 / J13: a door on a short wall clears this much each side. */
  door_jamb_clear_in: number;
  /** J8 Add closet. */
  closet_default: ClosetPolicy;
  /** J9: per door kind, the panel (and bifold side) choices. */
  door_kind_options: Record<string, { panels?: DoorPanels[]; bifold_side?: BifoldSide[] }>;
  door_panels_default: DoorPanels;
  bifold_side_default: BifoldSide;
  /** J10: Cased default per door kind. */
  door_cased_default: Record<string, boolean>;
  /** J11: interior doors start painted. */
  stained_default: boolean;
  /** J19: two rooms' edges within this cover the same span (one label). */
  label_same_span_tol_in: number;
  /** J17: a shared-wall drag stops before either room drops below this. */
  room_min_ft: number;
  /** The core's coordinate precision: corners (and recorded inch adjustments) round to 1 / coord_per_ft. */
  coord_per_ft: number;
  /** J8: the name (numbered) a closet room gets. */
  closet_room_name: string;
  /** J1 offset case: the kind a partly-meeting removed wall becomes (the wall keeps its thickness). */
  remove_wall_offset_kind: MissingKind;
  /** PRP-SKETCH-FIELD-5 (L1, L2, L4): damage marks. See the policy file's `_comment`. */
  damage: DamagePolicy;
  /** Read by the backend export; the core reads only the keys typed here. */
  export: {
    fills_wall_tol_in: number;
    /** Load-time bounds for stored records (sanitize.ts): plan extent, feet; wall thickness, inches. */
    max_plan_ft: number;
    wall_thickness_max_in: number;
  };
  /** PRP-SKETCH-FIELD-4 (K7, K8, K9, K11): snap reaches, inches; the rough-revert Undo toast, ms; snap kind order. */
  wall_snap_in: number;
  center_snap_in: number;
  jamb_snap_in: number;
  rough_undo_toast_ms: number;
  snap_priority: SnapKind[];
  /** PRP-WEB-2 W1.4: Add room sizes per type (census medians, inches). See the policy file's `_comment_web2`. */
  room_defaults: Record<string, RoomDefaultPolicy>;
  /** PRP-WEB-2 W1.2: Clip corner 45 degrees cuts this far back along each wall, feet. */
  chamfer_leg_default_ft: number;
  /** PRP-WEB-2 W1.5: an overlap deeper than this (inches) is a problem. */
  problem_overlap_min_in: number;
  /** The screen block (screenPolicy.ts reads the rest); the core reads the shared door symbols (graphDraw.ts). */
  screen: {
    door_glyph: DoorGlyphPolicy;
    /** The web plan (FloorPlanView, DD-SF3.18): room fill opacity, and a wall label's offset in label font sizes. */
    web_fill_opacity: number;
    web_dim_offset_fs: number;
  };
}

/** One `room_defaults` row: the menu label, the room name it gets, its inside size (inches). */
export interface RoomDefaultPolicy { label: string; name: string; width_in: number; depth_in: number }

/** `damage`: thresholds, the water line wheel, and how damage draws. */
export interface DamagePolicy {
  span_min_ft: number;
  whole_wall_tol_ft: number;
  flood_loss_types: string[];
  water_line_default_in: number;
  water_line_min_in: number;
  water_line_max_in: number;
  water_line_step_in: number;
  water_line_presets_in: number[];
  color: { light: string; dark: string };
  stroke_t: number;
  stroke_min_px: number;
  ceiling_icon: string;
  floor_icon: string;
  water_line_label: string;
  label_min_px: number;
  icon_size_px: number;
  water_line_dash_px: number[];
  water_line_inset_px: number;
}

/** `screen.door_glyph`: the door symbols per kind (J9, J15), shares of the wall thickness or opening width. */
export interface DoorGlyphPolicy {
  slide_offset_t: number;
  slide_overlap_w: number;
  pocket_open_w: number;
  jamb_half_wall: number;
  bifold_fold_deg: number;
}

/** One row of the E13 table. `height_in` is a fixed default; `height_from_ceiling` means ceiling minus sill. */
export interface MissingKindPolicy {
  label: string;
  to_floor: boolean;
  to_ceiling: boolean;
  sill_in: number;
  height_in?: number;
  height_from_ceiling?: boolean;
  sill_editable: boolean;
  height_editable: boolean;
  keeps_wall: boolean;
}

/** Where Add closet puts the closet (J8): carved from the room, or added beyond the wall. */
export type ClosetPlacement = 'inside' | 'outside'; // policy-literal-ok: the placement vocabulary (data contract), not a value
export const CLOSET_INSIDE: ClosetPlacement = 'inside'; // policy-literal-ok: vocabulary
export const CLOSET_OUTSIDE: ClosetPlacement = 'outside'; // policy-literal-ok: vocabulary

/** `closet_default` (J8). Sizes in feet. */
export interface ClosetPolicy {
  width_ft: number;
  depth_ft: number;
  placement: ClosetPlacement;
  door: string;
  panels: DoorPanels;
  min_bypass_width_ft: number;
  fallback_door: { type: string; width_ft: number };
}

export const POLICY: SketchPolicy = raw as SketchPolicy;

/** Inches per foot: a unit conversion, not a policy value. */
export const IN_PER_FT = 12;
const inToFt = (inches: number) => inches / IN_PER_FT;

/** Interior (shared) wall thickness, feet. The prototype's `T`. */
export const WALL_FT = inToFt(POLICY.interior_wall_in);
/** Door leaf opening angle, radians (Xactimate draws doors opened 20 degrees). */
export const DOOR_ANGLE_RAD = (POLICY.door_open_angle_deg * Math.PI) / 180;
/** Snap steps per foot (whole inches by default, F3). */
export const SNAP_PER_FT = IN_PER_FT / POLICY.snap_unit_in;
export const SAME_TOL_FT = inToFt(POLICY.same_reading_tol_in);
export const SHARED_TOL_FT = inToFt(POLICY.shared_wall_tol_in);
export const MISMATCH_TOL_FT = inToFt(POLICY.mismatch_tol_in);
export { inToFt };
/** FIELD-4 snap reaches, feet (K9 wall, K11 centre, K7 jamb). */
export const WALL_SNAP_FT = inToFt(POLICY.wall_snap_in);
export const CENTER_SNAP_FT = inToFt(POLICY.center_snap_in);
export const JAMB_SNAP_FT = inToFt(POLICY.jamb_snap_in);
/** FIELD-4 K9: rank of a snap kind (lower wins when two targets are in range). */
export const snapRank = (k: SnapKind): number => {
  const i = POLICY.snap_priority.indexOf(k);
  return i < 0 ? POLICY.snap_priority.length : i;
};

/** FIELD-4 K14: every measurement source; this release writes only `manual`. */
export const MEASUREMENT_SOURCES: readonly MeasurementSource[] = ['manual', 'laser', 'lidar']; // policy-literal-ok: the source vocabulary (data contract)
/** FIELD-4 K12: the geometry schema this build writes (RoomGeometryV2.geometryVersion); a room without one is the oldest. */
export const GEOMETRY_VERSION = 2; // policy-literal-ok: a schema version, not a threshold
export const GEOMETRY_VERSION_OLDEST = 1;
/** FIELD-4 K14: the source every measurement this release writes carries. */
export const MANUAL_SOURCE: MeasurementSource = 'manual'; // policy-literal-ok: the source vocabulary (data contract)
/** K14 read-time migration: a measurement stored without a source (or with an unknown one) is manual. */
export const measurementSource = (m: Pick<MeasurementV2, 'source'>): MeasurementSource =>
  (m.source && MEASUREMENT_SOURCES.includes(m.source) ? m.source : MANUAL_SOURCE);
/** K12: a v2 room's geometry schema; missing (or not a whole number) is the oldest. */
export const geometryVersionOf = (g: Pick<RoomGeometryV2, 'geometryVersion'> | null | undefined): number =>
  (g && Number.isSafeInteger(g.geometryVersion) && g.geometryVersion! >= GEOMETRY_VERSION_OLDEST ? g.geometryVersion! : GEOMETRY_VERSION_OLDEST);
